import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useResolvedActualDay, useWeekPunchSummary, useWeekViewContext } from "../../contexts/WeekView";
import type { Nurse } from "../../utils/models";
import { collectDaySearchNurseIds } from "./daySearchNurseIds";

type UseDaySearchNursesParams = {
	date: DateTime;
	dayIndex: number;
	showActual: boolean;
};

export function useDaySearchNurses({ date, dayIndex, showActual }: UseDaySearchNursesParams): Nurse[] {
	const { getNurseById } = useHospitalData();
	const { weekViewData } = useWeekViewContext();
	const { actualShifts, missingFromScheduled } = useResolvedActualDay(date);
	const punchSummary = useWeekPunchSummary(date);
	const dayItem = weekViewData[dayIndex];

	return useMemo<Nurse[]>(() => {
		const nurseIds = collectDaySearchNurseIds({
			showActual,
			scheduledNurseIds: dayItem.shifts.map((shift) => shift.nurseId),
			actualNurseIds: [...actualShifts, ...missingFromScheduled].map((shift) => shift.nurseId),
			callInNurseIds: punchSummary.callIn.employees.map((employee) => employee.nurseId),
			ptoNurseIds: dayItem.pto.map((pref) => pref.nurse_id),
		});
		// A row can outlive its nurse in the roster (e.g. removed from the department); there is no one to
		// pick then, and the table cannot render them either.
		return nurseIds.map((nurseId) => getNurseById(nurseId)).filter((nurse): nurse is Nurse => !!nurse);
	}, [showActual, dayItem, actualShifts, missingFromScheduled, punchSummary, getNurseById]);
}
